import { useSyncExternalStore } from 'react';
import {
  CompletedStop,
  EncounterRecordingEligibility,
  EncounterTranscriptModel,
  Listener,
  ParsedTranscript,
  RecordingGeolocation,
  StoreSnapshot,
  Transcript,
  WoundScribePendingStorageConfirmation,
  WoundScribeTranscript,
} from 'woundscribe/types';
import {
  buildAudioChunkKey,
  markAudioChunkStreamed,
  putAudioChunkRecord,
} from '../aural/audioChunkDb';
import {
  abortRecordingSession,
  createRecordingSession,
  pauseRecordingSession,
  pickAudioMimeType,
  RecordingSession,
  requestRecordingGeolocation,
  resumeRecordingSession,
  stopRecordingSession,
} from '../aural/recordingEngine';
import {
  createSocketSession,
  disconnectSocketSession,
  emitAudioChunk,
  emitPauseSession,
  emitResumeSession,
  emitStartSession,
  emitStopSession,
  SocketSession,
} from '../aural/socketEngine';
import {
  playRecordingPauseBeep,
  playRecordingStartBeep,
  playRecordingStopBeep,
  unlockRecordingSounds,
} from '../aural/sounds';
import {
  TOAST_DURATION_LONG,
  TOAST_DURATION_SHORT,
  WOUNDSCRIBE_AUDIO_BITS_PER_SECOND,
  WOUNDSCRIBE_MAX_RECORDING_MINUTES,
  WOUNDSCRIBE_MIC_STATES,
} from './constants';
import { generateLocalRecordingId } from './utils';

let snapshot: StoreSnapshot = {
  state: WOUNDSCRIBE_MIC_STATES.IDLE,
  elapsedMs: 0,
  toast: null,
  encounterId: undefined,
  patientId: undefined,
  transcripts: [],
  liveTranscriptItems: [],
  liveTranscript: '',
  provisionalTranscript: '',
  pendingStorageConfirmation: null,
  existingTranscripts: [],
  selectedExistingTranscriptSessionId: null,
  isLoadingExistingTranscripts: false,
  existingTranscriptsError: null,
  displayedTranscript: null,
  existingTranscriptsStale: false,
  isStopConfirmOpen: false,
  lastCompletedStop: null,
  recordingEligibility: null,
  isLoadingRecordingEligibility: false,
  localOrphanedRecordingIds: [],
  isAwaitingLocationPermission: false,
};

/** Confirmed Soniox tokens. Append-only. */
let committedTranscript = '';
/** Latest non-final tokens. Replaced on every live result. */
let partialTranscript = '';

let transcriptSeq = 0;
let activeSession: RecordingSession | null = null;
let startInFlight = false;
let visibilityListenerAttached = false;

const listeners = new Set<Listener>();
/** Elapsed time frozen across pause/resume */
let accumulatedMs = 0;
/** When current recording segment started */
let segmentStartedAt: number | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
let maxDurationStopInFlight = false;

let activeSocketSession: SocketSession | null = null;
/** Pending browser location prompt for a start attempt. Aborted by cancelWoundScribeStart. */
let locationAbortController: AbortController | null = null;
/** In-flight blob→ArrayBuffer→emit work; stop awaits these before stop-session. */
const pendingAudioChunkEmits = new Set<Promise<void>>();

/** Sequence number for the current take's chunks, forming the IndexedDB key with localRecordingId. */
let audioChunkIndex = 0;
/** In-flight IndexedDB chunk writes; stop awaits these so eviction never races an unfinished write. */
const pendingAudioChunkPersists = new Set<Promise<void>>();

function trackAudioChunkEmit(work: Promise<void>): void {
  pendingAudioChunkEmits.add(work);
  void work.finally(() => {
    pendingAudioChunkEmits.delete(work);
  });
}

async function flushPendingAudioChunkEmits(): Promise<void> {
  while (pendingAudioChunkEmits.size > 0) {
    await Promise.allSettled(pendingAudioChunkEmits);
  }
}

/**
 * Persists a chunk to IndexedDB without ever blocking or rejecting the
 * caller — this is the durability write, so it must succeed independently
 * of whether the WebSocket is connected. Returns the record's key so the
 * caller can flag it `streamed` once (and if) the socket emit succeeds.
 */
function persistAudioChunkForDurability(opts: {
  localRecordingId: string;
  backendSessionId: string | null;
  encounterId: string;
  chunkIndex: number;
  blob: Blob;
  mimeType: string;
}): string {
  const key = buildAudioChunkKey(opts.localRecordingId, opts.chunkIndex);

  const work = putAudioChunkRecord({
    key,
    localRecordingId: opts.localRecordingId,
    backendSessionId: opts.backendSessionId,
    encounterId: opts.encounterId,
    chunkIndex: opts.chunkIndex,
    blob: opts.blob,
    mimeType: opts.mimeType,
    streamed: false,
    createdAt: new Date().toISOString(),
  }).catch((err: unknown) => {
    // biome-ignore lint/suspicious/noConsole: reason here
    console.warn(
      '[WoundScribe] Failed to persist audio chunk to IndexedDB:',
      err instanceof Error ? err.message : err
    );
  });

  pendingAudioChunkPersists.add(work);
  void work.finally(() => {
    pendingAudioChunkPersists.delete(work);
  });

  return key;
}

/** Flags a chunk as streamed once its WebSocket emit succeeds; logs and ignores any failure. */
function markAudioChunkAsStreamed(key: string): void {
  const work = markAudioChunkStreamed(key).catch((err: unknown) => {
    // biome-ignore lint/suspicious/noConsole: reason here
    console.warn(
      '[WoundScribe] Failed to flag audio chunk as streamed:',
      err instanceof Error ? err.message : err
    );
  });

  pendingAudioChunkPersists.add(work);
  void work.finally(() => {
    pendingAudioChunkPersists.delete(work);
  });
}

async function flushPendingAudioChunkPersists(): Promise<void> {
  while (pendingAudioChunkPersists.size > 0) {
    await Promise.allSettled(pendingAudioChunkPersists);
  }
}

const woundscribe_max_recording_ms =
  Number.parseInt(WOUNDSCRIBE_MAX_RECORDING_MINUTES) * 60 * 1000;

function onDocumentHidden() {
  if (document.visibilityState !== 'hidden') {
    return;
  }
  if (snapshot.state !== WOUNDSCRIBE_MIC_STATES.RECORDING) {
    return;
  }

  // Disabled only for testing of various scenarios on iPad
  // autoPauseWoundScribeRecording(
  //   'Recording paused — screen locked or app backgrounded. Resume when ready, then stop to save.'
  // );
}

function ensureVisibilityGuard() {
  if (visibilityListenerAttached || typeof document === 'undefined') {
    return;
  }
  document.addEventListener('visibilitychange', onDocumentHidden);
  visibilityListenerAttached = true;
}

function emit() {
  listeners.forEach((l) => l());
}

function setSnapshot(partial: Partial<StoreSnapshot>) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

function clearTick() {
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
}

function clearTimers() {
  clearTick();
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
}

function scheduleToastClear(ms = 3500) {
  if (toastTimer) {
    clearTimeout(toastTimer);
  }
  toastTimer = setTimeout(() => {
    setSnapshot({ toast: null });
  }, ms);
}

function currentElapsed(): number {
  if (segmentStartedAt == null) {
    return accumulatedMs;
  }
  return accumulatedMs + (Date.now() - segmentStartedAt);
}

function startTicker() {
  clearTick();
  tickTimer = setInterval(() => {
    const elapsed = currentElapsed();
    setSnapshot({ elapsedMs: elapsed });

    if (
      elapsed >= woundscribe_max_recording_ms &&
      (snapshot.state === WOUNDSCRIBE_MIC_STATES.RECORDING ||
        snapshot.state === WOUNDSCRIBE_MIC_STATES.PAUSED) &&
      !maxDurationStopInFlight
    ) {
      void triggerMaxDurationStop();
    }
  }, 250);
}

async function triggerMaxDurationStop() {
  if (maxDurationStopInFlight) {
    return;
  }
  maxDurationStopInFlight = true;
  try {
    await stopWoundScribeRecording({
      reason: 'max_duration',
    });
  } finally {
    maxDurationStopInFlight = false;
  }
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): StoreSnapshot {
  return snapshot;
}

/** Synchronous read of the current state outside a React render (e.g. tests). */
export function getWoundScribeSnapshot(): StoreSnapshot {
  return getSnapshot();
}

export function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatPendingTranscriptDocument(opts: {
  durationMs: number;
  note?: string | null;
}): string {
  const when = new Date().toLocaleString();
  const duration = formatElapsed(opts.durationMs);

  return [
    'WoundScribe transcript',
    `Source: audio recording · ${duration}`,
    `Captured: ${when}`,
    opts.note ? `Note: ${opts.note}` : null,
    '',
    'Transcript pending — will be generated once the recording is processed.',
  ]
    .filter((line) => line != null)
    .join('\n');
}

function clearLiveTranscript() {
  committedTranscript = '';
  partialTranscript = '';
  setSnapshot({
    liveTranscriptItems: [],
  });
}

function publishLiveTranscript() {
  setSnapshot({
    liveTranscript: `${committedTranscript}${partialTranscript}`,
    provisionalTranscript: partialTranscript,
  });
}

function applyTranscription(
  payload: Transcript | { text: string; isFinal?: boolean }
) {
  const text = payload.text ?? '';

  // Handle full transcript payload with speaker info - only add final/confirmed items
  if ('speaker' in payload && payload.isFinal) {
    const transcriptItem: Transcript = {
      sessionId: payload.sessionId,
      text: payload.text,
      speaker: payload.speaker,
      startMs: payload.startMs,
      endMs: payload.endMs,
      isFinal: true,
    };

    // Group consecutive speakers - append to last item if same speaker
    const updatedItems = [...snapshot.liveTranscriptItems];
    const lastItem = updatedItems.at(-1);

    if (lastItem?.speaker === transcriptItem.speaker) {
      // Append text to existing item (group consecutive speakers).
      // The backend text already carries its own spaces, and sub-word pieces
      // (e.g. "cap" + "ture") must join with none, same as the footer caption.
      lastItem.text = `${lastItem.text}${transcriptItem.text}`;
      lastItem.endMs = transcriptItem.endMs;
    } else {
      // Add as new item (new speaker or first item)
      updatedItems.push(transcriptItem);
    }

    setSnapshot({
      liveTranscriptItems: updatedItems,
    });
  }

  // Handle text updates for live transcript display
  if (payload.isFinal === false) {
    partialTranscript = text;
  } else {
    committedTranscript += text;
    partialTranscript = partialTranscript.startsWith(text)
      ? partialTranscript.slice(text.length)
      : '';
  }
  publishLiveTranscript();
}

function addTranscript(entry: WoundScribeTranscript) {
  setSnapshot({
    transcripts: [entry, ...snapshot.transcripts],
  });
}

function updateTranscript(id: string, partial: Partial<WoundScribeTranscript>) {
  setSnapshot({
    transcripts: snapshot.transcripts.map((t) =>
      t.id === id ? { ...t, ...partial } : t
    ),
  });
}

function teardownActiveSession() {
  abortRecordingSession(activeSession);
  activeSession = null;
  pendingAudioChunkEmits.clear();
  pendingAudioChunkPersists.clear();
  if (activeSocketSession) {
    disconnectSocketSession(activeSocketSession);
    activeSocketSession = null;
  }
}

export function resetWoundScribeRecording(
  encounterId?: string,
  patientId?: string
) {
  teardownActiveSession();
  clearTimers();
  accumulatedMs = 0;
  segmentStartedAt = null;
  startInFlight = false;
  clearLiveTranscript();
  setSnapshot({
    state: WOUNDSCRIBE_MIC_STATES.IDLE,
    elapsedMs: 0,
    toast: null,
    encounterId,
    patientId,
    liveTranscript: '',
    provisionalTranscript: '',
    pendingStorageConfirmation: null,
    displayedTranscript: null,
  });
}

/**
 * True while a take is in progress, paused, or saving, and the open page belongs
 * to a different encounter than the one the take started on.
 */
export function isRecordingOnOtherEncounter(
  snapshot: Pick<StoreSnapshot, 'state' | 'encounterId'>,
  pageEncounterId: string | undefined
): boolean {
  return (
    snapshot.state !== WOUNDSCRIBE_MIC_STATES.IDLE &&
    snapshot.encounterId !== pageEncounterId
  );
}

/**
 * True when the take on screen is the just-completed recording and it was started
 * on the given encounter. Pages use this so a take never shows on another encounter.
 */
export function isCurrentRecordingOnEncounter(
  transcript: ParsedTranscript | null,
  encounterId: string | undefined
): boolean {
  return (
    Boolean(transcript?.isCurrentRecording) &&
    encounterId !== undefined &&
    transcript?.encounterId === encounterId
  );
}

/**
 * Binds the store to the encounter in view, but only while idle. A take in progress
 * or saving keeps the encounter it started on, so its audio, socket session, and
 * saved transcript stay there. Pages on other encounters show the take as
 * "recording elsewhere" instead (see isRecordingOnOtherEncounter).
 *
 * When idle, the store follows the open page. The just-completed take is kept, and
 * it shows again only on the encounter it started on.
 */
export function ensureWoundScribeEncounter(
  encounterId: string | undefined,
  active: boolean,
  patientId?: string
) {
  if (snapshot.state !== WOUNDSCRIBE_MIC_STATES.IDLE) {
    return;
  }
  const nextEncounterId = active ? encounterId : undefined;
  const nextPatientId = active ? patientId : undefined;
  if (
    snapshot.encounterId === nextEncounterId &&
    snapshot.patientId === nextPatientId
  ) {
    return;
  }
  const completedTake = snapshot.displayedTranscript?.isCurrentRecording
    ? snapshot.displayedTranscript
    : null;
  resetWoundScribeRecording(nextEncounterId, nextPatientId);
  if (completedTake) {
    setSnapshot({ displayedTranscript: completedTake });
  }
}

/**
 * Best-effort socket connection: failures are caught and logged rather
 * than thrown, since a missing/broken realtime connection must not stop
 * local recording (durability to IndexedDB is independent of the socket).
 * Returns null when the socket cannot be established at all.
 */
async function tryCreateWoundScribeSocketSession(
  mimeType: string,
  geolocation: RecordingGeolocation
): Promise<SocketSession | null> {
  let socketSession: SocketSession | null = null;
  try {
    socketSession = await createSocketSession({
      onConnectError: (err) => {
        // biome-ignore lint/suspicious/noConsole: reason here
        console.error('[WoundScribe] Socket connect error:', err.message);
      },
      onDisconnect: (reason) => {
        // 'io client disconnect' means we called socket.disconnect() ourselves — ignore
        if (reason !== 'io client disconnect') {
          autoPauseWoundScribeRecording(
            'Real-time connection lost — recording paused. Resume when ready.'
          );
        }
      },
      onTranscription: (payload) => {
        applyTranscription(payload);
      },
    });

    await emitStartSession(socketSession, {
      encounterId: snapshot.encounterId ?? '',
      patientId: snapshot.patientId ?? '',
      mimeType,
      geolocationStatus: geolocation.status,
      latitude: geolocation.latitude,
      longitude: geolocation.longitude,
    });

    return socketSession;
  } catch (err) {
    if (socketSession) {
      disconnectSocketSession(socketSession);
    }
    // biome-ignore lint/suspicious/noConsole: reason here
    console.warn(
      '[WoundScribe] Could not establish realtime session — recording locally only:',
      err instanceof Error ? err.message : err
    );
    return null;
  }
}

/**
 * Stops waiting for the browser's location answer during a start attempt. Nothing is
 * started, and a location answer that arrives later is ignored.
 */
export function cancelWoundScribeStart(): void {
  locationAbortController?.abort();
}

export async function startWoundScribeRecording() {
  if (
    startInFlight ||
    snapshot.state === WOUNDSCRIBE_MIC_STATES.RECORDING ||
    snapshot.state === WOUNDSCRIBE_MIC_STATES.PAUSED ||
    snapshot.state === WOUNDSCRIBE_MIC_STATES.PROCESSING
  ) {
    return;
  }

  startInFlight = true;
  unlockRecordingSounds();
  clearTimers();
  accumulatedMs = 0;
  segmentStartedAt = null;
  clearLiveTranscript();
  setSnapshot({
    toast: 'Waiting for location permission…',
    isAwaitingLocationPermission: true,
    liveTranscript: '',
    provisionalTranscript: '',
    displayedTranscript: null,
    selectedExistingTranscriptSessionId: null,
    existingTranscriptsError: null,
  });

  // No timeout here: the browser prompt waits for the clinician's answer, and Cancel is the way out.
  locationAbortController = new AbortController();
  const geolocation = await requestRecordingGeolocation(
    locationAbortController.signal
  );
  locationAbortController = null;
  if (!geolocation) {
    startInFlight = false;
    setSnapshot({ toast: null, isAwaitingLocationPermission: false });
    return;
  }

  setSnapshot({
    toast: 'Requesting microphone permission…',
    isAwaitingLocationPermission: false,
  });

  try {
    const { mimeType } = pickAudioMimeType();

    const localRecordingId = generateLocalRecordingId();
    audioChunkIndex = 0;

    activeSocketSession = await tryCreateWoundScribeSocketSession(
      mimeType,
      geolocation
    );

    setSnapshot({
      pendingStorageConfirmation: {
        localRecordingId,
        backendSessionId: activeSocketSession?.sessionId ?? null,
      },
    });

    const session = await createRecordingSession({
      audioBitsPerSecond: WOUNDSCRIBE_AUDIO_BITS_PER_SECOND,
      onStreamEnded: () => {
        autoPauseWoundScribeRecording(
          'Microphone disconnected — recording paused. Stop to save what was captured.'
        );
      },
      onChunk: (blob: Blob) => {
        const key = persistAudioChunkForDurability({
          localRecordingId,
          backendSessionId: activeSocketSession?.sessionId ?? null,
          encounterId: snapshot.encounterId ?? '',
          chunkIndex: audioChunkIndex++,
          blob,
          mimeType,
        });

        const socketSessionForChunk = activeSocketSession;
        if (
          !socketSessionForChunk?.socket.connected ||
          !socketSessionForChunk.sessionId
        ) {
          return;
        }

        // Track the async read+emit so stop can await every chunk spoken
        // before the user hit Stop (MediaRecorder onstop alone is not enough —
        // arrayBuffer() can still be in flight afterward).
        trackAudioChunkEmit(
          blob
            .arrayBuffer()
            .then((buffer) => {
              // Abort/reset cleared the session — do not emit on a dead socket.
              // Normal stop awaits this promise before clearing activeSocketSession.
              if (
                activeSocketSession !== socketSessionForChunk ||
                !socketSessionForChunk.socket.connected ||
                !socketSessionForChunk.sessionId
              ) {
                return;
              }
              emitAudioChunk(socketSessionForChunk, buffer);
              markAudioChunkAsStreamed(key);
            })
            .catch((err: Error) => {
              // biome-ignore lint/suspicious/noConsole: reason here
              console.warn(
                '[WoundScribe] Failed to read audio chunk:',
                err.message
              );
            })
        );
      },
    });

    activeSession = session;

    ensureVisibilityGuard();
    accumulatedMs = 0;
    segmentStartedAt = Date.now();
    setSnapshot({
      state: WOUNDSCRIBE_MIC_STATES.RECORDING,
      elapsedMs: 0,
      toast: activeSocketSession
        ? 'Recording — pause anytime, then stop when done.'
        : 'Recording locally — live transcription unavailable, connection issue.',
    });
    scheduleToastClear(
      activeSocketSession ? TOAST_DURATION_SHORT : TOAST_DURATION_LONG
    );
    startTicker();
    playRecordingStartBeep();
  } catch (err) {
    teardownActiveSession();
    const message =
      err instanceof Error
        ? err.message
        : 'Could not start microphone recording.';
    const denied =
      typeof err === 'object' &&
      err != null &&
      'name' in err &&
      (err as { name?: string }).name === 'NotAllowedError';

    setSnapshot({
      state: WOUNDSCRIBE_MIC_STATES.IDLE,
      elapsedMs: 0,
      toast: denied
        ? 'Microphone permission denied. Allow mic access and try again.'
        : message,
    });
    scheduleToastClear(TOAST_DURATION_LONG);
  } finally {
    startInFlight = false;
  }
}

export function pauseWoundScribeRecording() {
  if (snapshot.state !== WOUNDSCRIBE_MIC_STATES.RECORDING || !activeSession) {
    return;
  }
  pauseRecordingSession(activeSession);
  if (activeSocketSession) {
    emitPauseSession(activeSocketSession);
  }
  accumulatedMs = currentElapsed();
  segmentStartedAt = null;
  clearTick();
  setSnapshot({
    state: WOUNDSCRIBE_MIC_STATES.PAUSED,
    elapsedMs: accumulatedMs,
    toast: 'Paused — resume or stop.',
  });
  scheduleToastClear(TOAST_DURATION_SHORT);
  playRecordingPauseBeep();
}

/** Auto-pause for lock / background / mic disconnect (keeps captured audio). */
export function autoPauseWoundScribeRecording(reason: string) {
  if (snapshot.state !== WOUNDSCRIBE_MIC_STATES.RECORDING) {
    return;
  }

  if (activeSession) {
    try {
      pauseRecordingSession(activeSession);
    } catch {
      // Stream may already be dead; still move UI to paused so user can stop & save.
    }
  }

  if (activeSocketSession) {
    emitPauseSession(activeSocketSession);
  }

  accumulatedMs = currentElapsed();
  segmentStartedAt = null;
  clearTick();
  setSnapshot({
    state: WOUNDSCRIBE_MIC_STATES.PAUSED,
    elapsedMs: accumulatedMs,
    toast: reason,
  });
  scheduleToastClear(TOAST_DURATION_LONG);
  playRecordingPauseBeep();
}

export function resumeWoundScribeRecording() {
  if (snapshot.state !== WOUNDSCRIBE_MIC_STATES.PAUSED || !activeSession) {
    return;
  }

  const trackLive = activeSession.stream
    .getAudioTracks()
    .some((track) => track.readyState === 'live');

  if (
    !trackLive ||
    activeSession.recorder.state === WOUNDSCRIBE_MIC_STATES.INACTIVE
  ) {
    setSnapshot({
      toast:
        'Cannot resume — microphone session ended. Stop to save what was captured.',
    });
    scheduleToastClear(TOAST_DURATION_LONG);
    return;
  }

  try {
    resumeRecordingSession(activeSession);
  } catch {
    setSnapshot({
      toast:
        'Cannot resume — microphone session ended. Stop to save what was captured.',
    });
    scheduleToastClear(TOAST_DURATION_LONG);
    return;
  }

  if (activeSocketSession) {
    emitResumeSession(activeSocketSession);
  }

  segmentStartedAt = Date.now();
  setSnapshot({
    state: WOUNDSCRIBE_MIC_STATES.RECORDING,
    elapsedMs: accumulatedMs,
    toast: 'Recording resumed.',
  });
  scheduleToastClear(2500);
  startTicker();
  playRecordingStartBeep();
}

export async function stopWoundScribeRecording(opts?: {
  reason?: 'max_duration' | 'manual';
}) {
  if (
    (snapshot.state !== WOUNDSCRIBE_MIC_STATES.RECORDING &&
      snapshot.state !== WOUNDSCRIBE_MIC_STATES.PAUSED) ||
    !activeSession
  ) {
    return;
  }

  playRecordingStopBeep();

  const finalElapsed = currentElapsed();
  const session = activeSession;
  const socketSession = activeSocketSession;

  activeSession = null;
  clearTimers();
  accumulatedMs = finalElapsed;
  segmentStartedAt = null;

  const pendingAtStop = snapshot.pendingStorageConfirmation;

  setSnapshot({
    state: WOUNDSCRIBE_MIC_STATES.PROCESSING,
    elapsedMs: finalElapsed,
    toast: 'Saving recording and transcript…',
    existingTranscriptsStale: true,
    isStopConfirmOpen: false,
  });

  const encounterId = snapshot.encounterId || 'unknown';
  const draftId = `ws-rec-${Date.now()}-${++transcriptSeq}`;
  const createdAt = new Date().toISOString();
  addTranscript({
    id: draftId,
    encounterId,
    source: 'recording',
    createdAt,
    durationMs: finalElapsed,
    status: 'processing',
    text: '',
  });

  try {
    const result = await stopRecordingSession(session);

    // ondataavailable has fired, but onChunk's arrayBuffer() may still be
    // in flight — flush those emits while the socket session is still active
    // so every word up to Stop reaches WoundScribe before stop-session.
    await flushPendingAudioChunkEmits();
    // Likewise, ensure every chunk has actually landed in IndexedDB before
    // we ask the backend for S3 confirmation, so eviction never races a
    // write that is still in flight.
    await flushPendingAudioChunkPersists();

    activeSocketSession = null;
    if (socketSession) {
      try {
        // Ack waits for server finalize so late `transcription` events can land.
        await emitStopSession(socketSession);
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: reason here
        console.warn(
          '[WoundScribe] stop-session failed:',
          err instanceof Error ? err.message : err
        );
      }
      disconnectSocketSession(socketSession);
    }

    // Capture AFTER stop-session — finals often arrive during that window.
    const completedTranscript = resolveCompletedTranscriptItems(finalElapsed);

    const fileName = `woundscribe-${new Date()
      .toISOString()
      .replaceAll(/[:.]/g, '-')}.${result.extension}`;

    const transcriptText = formatPendingTranscriptDocument({
      durationMs: finalElapsed,
    });

    const sizeBytes = result.blob.size;

    updateTranscript(draftId, {
      status: 'ready',
      fileName,
      audioMimeType: result.mimeType,
      sizeBytes: sizeBytes > 0 ? sizeBytes : undefined,
      text: transcriptText,
    });

    accumulatedMs = 0;

    let toastMessage: string;
    if (opts?.reason === 'max_duration') {
      toastMessage = `Maximum ${WOUNDSCRIBE_MAX_RECORDING_MINUTES}-minute limit reached — recording saved automatically.`;
    } else {
      toastMessage =
        'Recording saved locally — open WoundScribe to play audio.';
    }

    setCurrentRecordingAsDisplayed(
      completedTranscript,
      createdAt,
      finalElapsed
    );
    setSnapshot({
      state: WOUNDSCRIBE_MIC_STATES.IDLE,
      elapsedMs: 0,
      toast: toastMessage,
      lastCompletedStop: buildCompletedStop(
        opts?.reason,
        pendingAtStop,
        socketSession
      ),
    });
    scheduleToastClear(
      opts?.reason === 'max_duration'
        ? TOAST_DURATION_LONG
        : TOAST_DURATION_SHORT
    );
  } catch (err) {
    await flushPendingAudioChunkEmits();
    await flushPendingAudioChunkPersists();
    activeSocketSession = null;
    if (socketSession) {
      disconnectSocketSession(socketSession);
    }

    const message =
      err instanceof Error ? err.message : 'Failed to save recording.';
    updateTranscript(draftId, {
      status: 'ready',
      text: formatPendingTranscriptDocument({
        durationMs: finalElapsed,
      }),
    });
    accumulatedMs = 0;
    setCurrentRecordingAsDisplayed(
      resolveCompletedTranscriptItems(finalElapsed),
      createdAt,
      finalElapsed
    );
    setSnapshot({
      state: WOUNDSCRIBE_MIC_STATES.IDLE,
      elapsedMs: 0,
      toast: message,
      lastCompletedStop: buildCompletedStop(
        opts?.reason,
        pendingAtStop,
        socketSession
      ),
    });
    scheduleToastClear(TOAST_DURATION_LONG);
  }
}

/** True while an in-progress take could be lost (recording or paused). */
export function isWoundScribeRecordingInProgress(): boolean {
  switch (snapshot.state) {
    case WOUNDSCRIBE_MIC_STATES.RECORDING:
    case WOUNDSCRIBE_MIC_STATES.PAUSED:
      return true;
    default:
      return false;
  }
}

/** Idle → start. Recording ↔ paused. Does not stop. */
export function toggleWoundScribePause() {
  if (snapshot.state === WOUNDSCRIBE_MIC_STATES.IDLE) {
    void startWoundScribeRecording();
    return;
  }
  if (snapshot.state === WOUNDSCRIBE_MIC_STATES.RECORDING) {
    pauseWoundScribeRecording();
    return;
  }
  if (snapshot.state === WOUNDSCRIBE_MIC_STATES.PAUSED) {
    resumeWoundScribeRecording();
  }
}

/**
 * Store existing transcripts from backend query.
 * Called by useExistingTranscriptsQuery hook after successful fetch.
 */
export function setExistingTranscripts(
  transcripts: EncounterTranscriptModel[]
): void {
  const sortedTranscripts = [...transcripts].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const keepCurrentRecording = Boolean(
    snapshot.displayedTranscript?.isCurrentRecording
  );
  const latestSessionId =
    sortedTranscripts.length > 0 ? sortedTranscripts[0].sessionId : null;
  const prevSelected = snapshot.selectedExistingTranscriptSessionId;
  const prevStillPresent =
    !!prevSelected &&
    sortedTranscripts.some((t) => t.sessionId === prevSelected);

  // Keep live take focus; otherwise keep the user's selection when still valid.
  let selectedExistingTranscriptSessionId: string | null;
  if (keepCurrentRecording) {
    selectedExistingTranscriptSessionId = prevSelected;
  } else if (prevStillPresent) {
    selectedExistingTranscriptSessionId = prevSelected;
  } else {
    selectedExistingTranscriptSessionId = latestSessionId;
  }

  setSnapshot({
    existingTranscripts: sortedTranscripts,
    selectedExistingTranscriptSessionId,
    isLoadingExistingTranscripts: false,
  });
}

/**
 * Point Completed Recording at a backend session and clear any stale transcript
 * so the body reloads (e.g. after manual upload finishes).
 */
export function preferTranscriptSession(sessionId: string): void {
  const trimmed = sessionId?.trim();
  if (!trimmed) {
    return;
  }
  setSnapshot({
    selectedExistingTranscriptSessionId: trimmed,
    displayedTranscript: null,
    existingTranscriptsStale: true,
  });
}

/**
 * Set loading state for existing transcripts.
 * Called by useExistingTranscriptsQuery hook when query starts.
 */
export function setExistingTranscriptsLoading(loading: boolean): void {
  setSnapshot({
    isLoadingExistingTranscripts: loading,
    ...(loading ? { existingTranscriptsError: null } : {}),
  });
}

/**
 * Set error state for existing transcripts.
 * Called by useExistingTranscriptsQuery hook on error.
 */
export function setExistingTranscriptsError(error: string | null): void {
  setSnapshot({
    existingTranscriptsError: error,
    isLoadingExistingTranscripts: false,
  });
}

/**
 * Store the result of GetEncounterRecordingEligibility (is a live recording or upload
 * currently in flight for this encounter). Called by useEncounterRecordingEligibilitySync.
 */
export function setRecordingEligibility(
  eligibility: EncounterRecordingEligibility | null
): void {
  setSnapshot({
    recordingEligibility: eligibility,
    isLoadingRecordingEligibility: false,
  });
}

/** Called by useEncounterRecordingEligibilitySync when the query starts. */
export function setRecordingEligibilityLoading(loading: boolean): void {
  setSnapshot({ isLoadingRecordingEligibility: loading });
}

/**
 * Local recording ids with unfinished IndexedDB audio for this encounter — a take that
 * never reached the backend (dropped before the socket opened, or mid-stream). The backend
 * has no record of these, so this is a client-only signal; see localOrphanedRecordingIds.
 */
export function setLocalOrphanedRecordingIds(ids: string[]): void {
  setSnapshot({ localOrphanedRecordingIds: ids });
}

/**
 * Update displayed transcript after loading from backend.
 * Called by useExistingTranscriptsQuery hook after successful session query.
 */
export function setDisplayedTranscript(
  transcript: ParsedTranscript,
  sessionId: string
): void {
  setSnapshot({
    displayedTranscript: transcript,
    selectedExistingTranscriptSessionId: sessionId,
  });
}

/**
 * Prefer speaker-tagged live items; fall back to committed caption text.
 * Must run after stop-session so late finals are included.
 */
function resolveCompletedTranscriptItems(durationMs: number): Transcript[] {
  if (snapshot.liveTranscriptItems.length > 0) {
    return [...snapshot.liveTranscriptItems];
  }

  const text = committedTranscript.trim();
  if (!text) {
    return [];
  }

  return [
    {
      sessionId:
        snapshot.pendingStorageConfirmation?.backendSessionId || 'current',
      text,
      speaker: '1',
      startMs: 0,
      endMs: Math.max(0, durationMs),
      isFinal: true,
    },
  ];
}

/**
 * Set just-completed recording as the displayed transcript.
 * Called when recording stops to display the transcript immediately.
 */
export function setCurrentRecordingAsDisplayed(
  transcriptItems: Transcript[],
  createdAt: string,
  durationMs?: number
): void {
  const elapsedForDuration =
    typeof durationMs === 'number' && durationMs > 0
      ? durationMs
      : snapshot.elapsedMs;
  const parsedTranscript: ParsedTranscript = {
    items: transcriptItems,
    raw: committedTranscript,
    sessionId:
      snapshot.pendingStorageConfirmation?.backendSessionId ||
      `current-${Date.now()}`,
    createdAt,
    isCurrentRecording: true,
    encounterId: snapshot.encounterId,
    durationSeconds:
      elapsedForDuration > 0
        ? Math.round(elapsedForDuration / 1000)
        : undefined,
  };
  setSnapshot({
    displayedTranscript: parsedTranscript,
    selectedExistingTranscriptSessionId: null,
  });
}

/**
 * Clear existing transcripts when starting a new recording.
 */
export function clearExistingTranscripts(): void {
  setSnapshot({
    existingTranscripts: [],
    selectedExistingTranscriptSessionId: null,
    displayedTranscript: null,
    existingTranscriptsError: null,
  });
}

/** Shared recording state to React components */
export function useWoundScribeStore() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Re-renders only when a session starts or ends, not on every elapsed-time tick.
export function useIsWoundScribeSessionActive() {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().state !== WOUNDSCRIBE_MIC_STATES.IDLE
  );
}

/** Called once the existing-transcripts list has been fetched again. */
export function clearExistingTranscriptsStale(): void {
  setSnapshot({ existingTranscriptsStale: false });
}

/**
 * Re-fetch the encounter's transcript list. Used when a transcript becomes ready without the
 * user opening it (an upload finishing in the background). Without this, the one-transcript
 * rule keeps seeing an empty list and leaves Start Recording enabled.
 */
export function markExistingTranscriptsStale(): void {
  setSnapshot({ existingTranscriptsStale: true });
}

export function useExistingTranscriptsStale() {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().existingTranscriptsStale
  );
}

/** Opens the Stop Recording confirmation. Only meaningful during an active session. */
export function requestStopConfirmation(): void {
  if (
    snapshot.state !== WOUNDSCRIBE_MIC_STATES.RECORDING &&
    snapshot.state !== WOUNDSCRIBE_MIC_STATES.PAUSED
  ) {
    return;
  }
  setSnapshot({ isStopConfirmOpen: true });
}

/** Closes the Stop Recording confirmation without stopping (Continue Recording). */
export function dismissStopConfirmation(): void {
  setSnapshot({ isStopConfirmOpen: false });
}

/** The ids the eviction hook needs, captured when a stop finishes. */
function buildCompletedStop(
  reason: CompletedStop['reason'] | undefined,
  pending: WoundScribePendingStorageConfirmation | null,
  socketSession: SocketSession | null
): CompletedStop {
  return {
    reason: reason ?? 'manual',
    localRecordingId: pending?.localRecordingId ?? null,
    backendSessionId:
      socketSession?.sessionId ?? pending?.backendSessionId ?? null,
  };
}

/** Changes only when a stop finishes, not on every timer tick. */
export function useLastCompletedStop(): CompletedStop | null {
  return useSyncExternalStore(subscribe, () => getSnapshot().lastCompletedStop);
}

/**
 * The localRecordingId of the take currently being captured (recording or paused), if any.
 * Used to exclude the active take from the orphaned-local-recordings check — its chunks are
 * expected to still be in IndexedDB and are not leftover from a previous, abandoned attempt.
 */
export function useCurrentLocalRecordingId(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().pendingStorageConfirmation?.localRecordingId ?? null
  );
}

export function useRecordingEligibility() {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().recordingEligibility
  );
}

export function useIsLoadingRecordingEligibility() {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().isLoadingRecordingEligibility
  );
}

export function useLocalOrphanedRecordingIds(): string[] {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().localOrphanedRecordingIds
  );
}
